import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import type { ClientSyncStateWire } from './client-sync-state';
import { queryClient, queryKeys } from './query-client';
import { useDuplexerSubscription } from './use-duplexer-subscription';

const ENTITY_EVENTS: Array<'created' | 'updated'> = ['created', 'updated'];

async function fetchClientSyncState(): Promise<ClientSyncStateWire | null> {
  const res = await apiFetch(`${API_BASE}/api/client-sync-state`, { credentials: 'include' });
  if (!res.ok) throw new Error(`client-sync-state ${res.status}`);
  return ((await res.json()) as { clientSyncState?: ClientSyncStateWire | null }).clientSyncState ?? null;
}

/** Best-effort: the state after the write, or null. A failed write leaves this browser's own stamps as they are. */
async function postMark(body: { categories?: string[]; imported?: ClientSyncStateWire['homeSeen'] }): Promise<ClientSyncStateWire | null> {
  try {
    const res = await apiFetch(`${API_BASE}/api/client-sync-state`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const state = ((await res.json()) as { clientSyncState?: ClientSyncStateWire | null }).clientSyncState ?? null;
    if (state) queryClient.setQueryData(queryKeys.clientSyncState, state);
    return state;
  } catch {
    return null;
  }
}

/**
 * The workspace's client sync state on symphony-bff, kept live by the `individuals-client-sync-state`
 * push, so a category opened on the phone clears here while Home is open. `null` until (and unless) the
 * read answers; any failure means this browser keeps its own stamps.
 */
export function useClientSyncState(): {
  state: ClientSyncStateWire | null;
  markSeen: (categories: string[]) => Promise<ClientSyncStateWire | null>;
  importSeen: (imported: NonNullable<ClientSyncStateWire['homeSeen']>) => Promise<ClientSyncStateWire | null>;
} {
  const query = useQuery({
    queryKey: queryKeys.clientSyncState,
    queryFn: fetchClientSyncState,
    staleTime: 0,
    retry: false,
  });
  useDuplexerSubscription<ClientSyncStateWire>(
    IndividualsDuplexerChannel.CLIENT_SYNC_STATE,
    (payload) => {
      if (payload.entity) queryClient.setQueryData(queryKeys.clientSyncState, payload.entity);
    },
    ENTITY_EVENTS,
  );
  const markSeen = useCallback((categories: string[]) => postMark({ categories }), []);
  const importSeen = useCallback((imported: NonNullable<ClientSyncStateWire['homeSeen']>) => postMark({ imported }), []);
  return { state: query.data ?? null, markSeen, importSeen };
}
